import State from './state.js';

/**
 * Collection of Google Maps functionality, some of which was directly taken from the
 * app_storelocator_new / storelocator.js originally in use on Store Locator page.
 *
 * import as a module with:
 * > import GM from './googlemaps';
 *
 */
export default {

    markers: [],

    location: null,

    infoBox: {},

    zoomed: false,

    getLocation () {
        return this.location;
    },

    /**
     * init the google map
     * @param  Object options
     * @param  Object callbacks
     * @return GoogleMapsObject
     */
    init (options, callbacks) {
        const newMap = new google.maps.Map(document.getElementById('map_canvas'), options);

        if (callbacks && callbacks.centerChanged) {
            // Fire callback then the center of the map is moved
            google.maps.event.addListener(newMap, 'center_changed', () => {
                const lat = newMap.getCenter().lat();
                const lng = newMap.getCenter().lng();
                callbacks.centerChanged(lat, lng);
            });
        }

        return newMap;
    },

    /**
     * gets a Google Map Location from Lat / Long
     * @param  Float lat
     * @param  Float lng
     * @return GoogleMapsLocation
     */
    getLocationFromLatLng (lat, lng) {
        return new google.maps.LatLng(lat, lng);
    },

    /**
     * Centers the map from a google map location
     * @param GoogleMapLocation googleLocation
     */
    setCenter (googleLocation) {
        State.map.setCenter(googleLocation);
    },

    /**
     * Get the geo location from an address
     * @param  String   address
     * @param  Function callback
     */
    geoCodeAddress (address, country, callback) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({
            address,
            componentRestrictions: {
                country
            }
        }, callback);
    },

    /**
     * Zoom Map by GeoCode
     * @param  GoogleMapLocation   geoCode
     * @param  Function                         callback
     */
    zoomToGeoCode (geoCode, callback) {
        const results = geoCode[0];

        const lat = results.geometry.location.lat();
        const lng = results.geometry.location.lng();

        this.zoomToArea(lat, lng, callback);
    },

    /**
     * Zoom to an area by Lat /Long
     * @param  Float    lat
     * @param  Float    lng
     * @param  Function callback
     */
    zoomToArea (lat, lng, callback) {
        if (!State.map || !lat || !lng) {
            setTimeout(() => {
                this.zoomToArea(lat, lng, callback);
            }, 200)
            ;

            return;
        }

        this.location = new google.maps.LatLng(lat, lng);

        State.map.setCenter(this.location);

        if (!this.zoomed) {
            State.map.setZoom(10);
            this.zoomed = true;
        }

        if (callback) callback();
    },

    /**
     * Create a marker
     * @param  Array            stores
     * @param  Integer      index           Index of the stores array to use
     * @param  String       markerUrl       URL of the marker
     * @param  String       markerHtml
     * @param  Function     onClickCallback
     */
    createMarker (store, storeIndex, markerUrl, markerHtml, onClickCallback) {
        const marker = new google.maps.Marker({
            position: new google.maps.LatLng(store.latitude, store.longitude),
            map     : State.map,
            title   : `${store.name} ${store.address1} ${store.city}`,
            icon    : markerUrl,
            storeid : store.ID,
            storenum: storeIndex
        });

        marker.setMap(State.map);

        this.markers.push(marker);

        if (!this.infoBox[store.ID]) {
            this.infoBox[store.ID] = {
                html: markerHtml
            };

            this.infoBox[store.ID].obj = new google.maps.InfoWindow({
                content : this.infoBox[store.ID].html,
                position: marker.position
            });
        }

        google.maps.event.addListener(marker, 'click', () => {
            this.closeInfoBoxes();
            this.openInfoBox(store.ID);
            if (onClickCallback) onClickCallback();
        });
    },

    /**
     * Open the infobox
     * @param  Integer id
     */
    openInfoBox (id) {
        this.closeInfoBoxes();
        State.map.setCenter(this.getLocationFromLatLng(this.infoBox[id].obj.position.lat(),
            this.infoBox[id].obj.position.lng()));
        this.infoBox[id].obj.open(State.map);
    },

    /**
     * Close all InfoBoxes
     */
    closeInfoBoxes () {
        for (const i in this.infoBox) {
            if (this.infoBox[i].obj && typeof this.infoBox[i].obj === 'object') {
                this.infoBox[i].obj.close();
            }
        }
    },

    /**
     * Open an infobox from a marker click based on ID
     * @param  Array stores [description]
     * @param  Integer index
     */
    markerInfo (stores, index) {
        const store = stores[index];
        this.zoomToArea(store.latitude, store.longitude);
        this.openInfoBox(store.ID);
    },

    /**
     * Clears all markers
     */
    clearMarkers () {
        for (let i = 0; i < this.markers.length; i++) {
            this.markers[i].setMap(null);
        }

        this.markers.length = 0;
    },

    /**
     * Clear Location Markers
     */
    clearLocationMarkers (stores) {
        for (let i = 0; i < this.markers.length; i++) {
            const storeId = this.markers[i].storeid;
            const store = this.findById(stores, storeId);

            if (!store || !store.isDemoDaysLocation) {
                this.markers[i].setMap(null);
            }
        }
    },

    findById (stores, id) {
        let store = false
;

        for (let i = 0; i < stores.length; i++) {
            if (stores[i].ID === id) {
                store = stores[i];
                break;
            }
        }

        return store;
    },

    getDistanceFromCenter (loc) {
        return this.getDistance(this.location, loc);
    },

    /**
     * Calculates distance between two GoogleMap Locaiton Points
     * Pulled from original storelocator.js
     * @param  GoogleMapLocation p1
     * @param  GoogleMapLocation p2
     * @return Integer
     */
    getDistance (p1, p2) {
        if (!p1 || !p2) {
            return 0;
        }

        const R = 6371; // Radius of the Earth in km
        const dLat = (p2.lat() - p1.lat()) * Math.PI / 180;
        const dLon = (p2.lng() - p1.lng()) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(p1.lat() * Math.PI / 180) * Math.cos(p2.lat() * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c;

        return d;
    },

    /**
     * Gets Mapping info from a Geo Position
     * Pulled from original storelocator.js
     * @param  GoogleMap   position
     * @param  Function     callback
     */
    // reverseGeocode(position) {
    //   var geocoder = new google.maps.Geocoder();
    //   var location = geocoder.geocode({
    //     'latLng': position
    //   }, (results, status) => {
    //     if (status == google.maps.GeocoderStatus.OK) {
    //       return this.getPostalCodeFromData(results);
    //     }
    //   });
    // },

    /**
     * Get Postal Code from reverseGeo data
     * Pulled from original storelocator.js
     * @param  Array/Object results
     * @return String
     */
    getPostalCodeFromData (results) {
        let zip;
        // var country = null;

        for (const result in results) {
            if (results[result].types && (results[result].types[0] === 'postal_code' || results[result].types[0] === 'locality')) {
                for (const component in results[result].address_components) {
                    const addresscomponent = results[result].address_components[component]
;

                    if (addresscomponent.types && addresscomponent.types[0] === 'postal_code') {
                        zip = addresscomponent.short_name;
                    }
                    // else if (addresscomponent.types && addresscomponent.types[0] == 'country') {
                    //   country = addresscomponent.short_name;
                    // }
                }
            }
        }

        // in case the result with type==postal_code not found in geo results we trying to obtain zip from 1-st one
        if (!zip) {
            const oneRes = results[0]
;

            if (oneRes) {
                for (const c in oneRes.address_components) {
                    const a = oneRes.address_components[c]
;

                    if (a.types && a.types[0] === 'postal_code') {
                        zip = a.short_name;
                    }
                    // else if (a.types && a.types[0] == 'country') {
                    //   country = a.short_name;
                    // }
                }
            } else {
                zip = '';
                // country = '';
            }
        }

        // bus.$emit('foundNewPostalCodeFromMovement', zip);
        return zip;
    },

    getUserLocation (callback) {
        let location = null;

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };

                location = pos;

                if (callback) callback(location);
            }, () => {});
        }
    }

};
